<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="1200"
    max-height="74vh"
    persistent
  >
    <v-card class="purchase-dialog">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex justify-space-between align-center" style="flex: 1;">
          <span>发起请购</span>
          <div style="margin-top: 4px;text-align: right;">
            <div v-if="projectName" style="font-size: 18px;">{{ projectName }}</div>
            <div v-if="contractCode" style="font-size: 14px;color: #A5A5A5;line-height: 1;">{{ contractCode }}</div>
          </div>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="closeDialog"
        />
      </v-card-title>
      <v-card-text>
        <v-form ref="purchaseOrderForm" v-model="purchaseOrderValid">
          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="purchaseOrderData.contractNo"
                :search="contractSearchText"
                @update:search="val => (contractSearchText = val)"
                :items="contractSearchText ? contractList : []"
                :loading="contractLoading"
                :no-filter="true"
                item-title="code"
                item-value="code"
                label="合同号"
                placeholder="请输入合同号搜索"
                persistent-placeholder
                variant="outlined"
                density="compact"
                hide-details="auto"
                :no-data-text="contractSearchText ? '没有找到匹配的合同' : '请输入合同号进行搜索'"
                clearable
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="{ ...props, title: undefined }">
                    <div>
                      <div class="text-body-2">{{ item.raw.project }}</div>
                      <div class="text-caption text-grey" v-if="item.raw.code">合同号: {{ item.raw.code }}</div>
                    </div>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="purchaseOrderData.warehouse"
                :items="formattedWarehouseList"
                item-title="warehouseName"
                item-value="warehouseAddress"
                label="收货仓库"
                placeholder="请选择收货仓库"
                persistent-placeholder
                variant="outlined"
                density="compact"
                hide-details="auto"
                return-object
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="{ ...props, title: undefined }">
                    <div>
                      <div class="text-body-2">{{ item.raw.warehouseName }}</div>
                      <div class="text-caption text-grey" v-if="item.raw.warehouseAddress">地址: {{ item.raw.warehouseAddress }}</div>
                    </div>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <!-- 物料列表 -->
          <div class="material-purchase-list">
            <div class="material-purchase-body">
              <div
                v-for="(material, index) in purchaseOrderMaterials"
                :key="material.id || index"
                class="material-purchase-item"
              >
                <div class="item-content" style="width: 23%;">
                  <div class="material-name" style="text-align: left;">{{ material.materialName }}</div>
                  <div class="material-spec" style="text-align: left;">{{ material.specDesc }}</div>
                  <div class="material-code" style="text-align: left;">{{ material.materialCode }}</div>
                </div>
                <div class="item-content" style="width: 11%;">
                  <v-text-field
                    v-model="material.materialCount"
                    label="数量"
                    persistent-placeholder
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    type="number"
                    @input="handleQuantityInput(material, index)"
                  />
                </div>
                <div class="item-content" style="width: 80px;">
                  <v-text-field
                    v-model="material.materialUnit"
                    label="单位"
                    persistent-placeholder
                    variant="outlined"
                    density="compact"
                    hide-details
                    type="text"
                    class="v-field--readonly"
                    readonly
                  />
                </div>
                <div class="item-content" style="width: 42%;">
                  <v-textarea
                    v-model="material.remark"
                    label="备注"
                    persistent-placeholder
                    variant="outlined"
                    density="compact"
                    hide-details
                    rows="1"
                    resize
                    auto-grow
                  ></v-textarea>
                </div>
                <div class="item-content" style="width: 16%; position: relative;">
                  <v-text-field
                    v-model="material.paymentDate"
                    label="计划交货"
                    persistent-placeholder
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    class="native-date-field"
                    @click="openMaterialDatePicker(index)"
                  />
                  <input
                    :ref="el => setMaterialDateInputRef(el, index)"
                    type="date"
                    :value="material.paymentDate"
                    :min="getTodayDate()"
                    style="position: absolute; left: 0; top: 100%; opacity: 0; pointer-events: none; width: 100%; height: 1px; z-index: 9999;"
                    @change="(e) => onMaterialDateChange(e, index)"
                  />
                </div>
              </div>
            </div>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="error" variant="tonal" @click="closeDialog">取消</v-btn>
        <v-btn color="primary" variant="flat" @click="submitPurchaseOrder" :loading="purchaseOrderLoading">确定</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'
import { contractApi, purchaseApi } from '@/services/api'

interface WarehouseItemOptions {
  warehouseNo: string
  warehouseName: string
  warehouseAddress: string
}

interface MaterialItem {
  id?: number | string
  materialName: string
  materialCode: string
  specDesc: string
  materialUnit: string
  materialCount: number | null
  brand: string
  remark: string
  warehouse: any
  paymentDate: string
  materialType?: string
  bigClass?: string
}

const { showSuccess, showError } = useSnackbar()

const props = defineProps<{
  modelValue: boolean
  materials: any[]
  warehouseList: WarehouseItemOptions[]
  projectName?: string | null
  contractCode?: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'success': []
}>()

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const formattedWarehouseList = computed(() => {
  return props.warehouseList.map(warehouse => ({
    ...warehouse,
    warehouseName: `${warehouse.warehouseName}`
  }))
})

const purchaseOrderForm = ref()
const purchaseOrderValid = ref(false)
const purchaseOrderLoading = ref(false)
const purchaseOrderMaterials = ref<any[]>([])
const materialDateInputRefs = ref<Record<number, HTMLInputElement | null>>({})
const purchaseOrderData = ref({
  contractNo: '',
  warehouse: null as any
})

// 合同搜索
const contractSearchText = ref('')
const contractList = ref<any[]>([])
const contractLoading = ref(false)

// 初始化物料数据
const initMaterials = () => {
  purchaseOrderMaterials.value = props.materials.map((item: any) => ({
    id: item.id || item._selectedKey,
    materialName: item.materialName || '',
    materialCode: item.materialCode || '',
    specDesc: item.materialSpec || item.specDesc || '',
    materialUnit: item.materialUsageUnit || item.materialUnit || item.unit || item._originalMaterialUnit || '',
    materialCount: item.materialCount ?? item.quantity ?? item.productUsageSum ?? null,
    brand: item.brand || '',
    remark: item.remark || item.materialRemark || '',
    warehouse: null as any,
    paymentDate: item.plannedDeliveryDate || '',
    materialType: item.materialType || '',
    bigClass: item.bigClass || ''
  }))
  purchaseOrderData.value = { contractNo: props.contractCode || '', warehouse: null }
  materialDateInputRefs.value = {}
}

// 打开弹窗时初始化
watch(() => props.modelValue, (val) => {
  if (val) {
    initMaterials()
  }
})

const closeDialog = () => {
  dialogVisible.value = false
  purchaseOrderMaterials.value = []
  purchaseOrderData.value = { contractNo: '', warehouse: null }
  materialDateInputRefs.value = {}
  purchaseOrderForm.value?.reset()
}

const getTodayDate = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const setMaterialDateInputRef = (el: HTMLInputElement | null, index: number) => {
  if (el) {
    materialDateInputRefs.value[index] = el
  }
}

const openMaterialDatePicker = (index: number) => {
  const input = materialDateInputRefs.value[index]
  if (input) {
    if (input.showPicker) {
      input.showPicker()
    } else {
      input.click()
    }
  }
}

const onMaterialDateChange = (event: Event, index: number) => {
  const target = event.target as HTMLInputElement
  const newDate = target.value
  try {
    if (newDate && typeof newDate === 'string' && purchaseOrderMaterials.value[index]) {
      purchaseOrderMaterials.value[index].paymentDate = newDate
    }
  } catch (error) {
    console.error('日期处理失败:', error)
  }
}

const handleQuantityInput = (material: any, _index: number) => {
  let value = String(material.materialCount || '').trim()
  value = value.replace(/[^\d.]/g, '')

  const dotIndex = value.indexOf('.')
  if (dotIndex !== -1) {
    const beforeDot = value.substring(0, dotIndex)
    const afterDot = value.substring(dotIndex + 1).replace(/\./g, '').substring(0, 4)
    value = beforeDot + '.' + afterDot
  }

  if (value === '' || value === '.') {
    material.materialCount = ''
    return
  }

  if (value.startsWith('.')) {
    value = '0' + value
  }

  value = value.replace(/^0+(\d)/, '$1')
  const num = parseFloat(value)

  if (!isNaN(num) && num >= 0) {
    material.materialCount = value
  } else {
    material.materialCount = ''
  }
}

const submitPurchaseOrder = async () => {
  if (!purchaseOrderData.value.warehouse || !purchaseOrderData.value.warehouse.warehouseNo) {
    showError('送货地址不能为空')
    return
  }
  const materials = purchaseOrderMaterials.value
  for (let i = 0; i < materials.length; i++) {
    if (!materials[i].materialUnit) {
      showError('单位不能为空')
      return
    }
  }
  const materialList = materials.map((material: any) => ({
    materialCode: material.materialCode,
    materialName: material.materialName,
    materialSpec: material.specDesc,
    brand: material.brand || '',
    quantity: material.materialCount,
    unit: material.materialUnit,
    remark: material.remark || '',
    plannedDeliveryDate: material.paymentDate || '',
    type: 1
  }))
  try {
    purchaseOrderLoading.value = true
    const submitData = {
      contractNo: purchaseOrderData.value.contractNo || '',
      warehouseNo: purchaseOrderData.value.warehouse?.warehouseNo || '',
      warehouseName: purchaseOrderData.value.warehouse?.warehouseName || '',
      items: materialList
    }
    const res = await purchaseApi.purchaseRequisitionAdd(submitData)
    if (res?.code === 0) {
      showSuccess('请购单创建成功')
      emit('success')
      closeDialog()
    } else {
      showError(res?.message || res?.msg || '创建请购单失败')
    }
  } catch (error: any) {
    console.error('创建请购单失败:', error)
    showError(error.message || '创建请购单失败')
  } finally {
    purchaseOrderLoading.value = false
  }
}

// 监听合同搜索
watch(
  contractSearchText,
  async (newVal: string) => {
    if (!newVal || newVal.trim() === '') {
      contractList.value = []
      return
    }
    try {
      contractLoading.value = true
      const res = await contractApi.contractSearch({
        page: 1,
        pageSize: 100,
        keyword: contractSearchText.value || ''
      })
      if (res?.code === 0 && res.data) {
        contractList.value = res.data || []
      }
    } catch (error) {
      console.error('加载合同列表失败:', error)
    } finally {
      contractLoading.value = false
    }
  },
  { immediate: false }
)
</script>

<style scoped>
.purchase-dialog {
  background-color: #2B2D33 !important;
}

.purchase-dialog :deep(.v-card-title) {
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding: 16px 24px;
  font-size: 18px;
  font-weight: 500;
}

.purchase-dialog :deep(.v-card-text) {
  padding: 24px;
}

.purchase-dialog :deep(.v-card-actions) {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.purchase-dialog :deep(.v-autocomplete .v-field__input),
.purchase-dialog :deep(.v-text-field .v-field__input),
.purchase-dialog :deep(.v-autocomplete input),
.purchase-dialog :deep(.v-text-field input),
.purchase-dialog :deep(.v-textarea .v-field__input),
.purchase-dialog :deep(.v-textarea textarea) {
  color: #fff !important;
}

.purchase-dialog :deep(.v-autocomplete .v-field__outline),
.purchase-dialog :deep(.v-text-field .v-field__outline),
.purchase-dialog :deep(.v-textarea .v-field__outline) {
  color: rgba(255, 255, 255, 0.12) !important;
}

.purchase-dialog :deep(.v-autocomplete .v-field__input::placeholder),
.purchase-dialog :deep(.v-text-field .v-field__input::placeholder),
.purchase-dialog :deep(.v-textarea .v-field__input::placeholder) {
  color: rgba(255, 255, 255, 0.5) !important;
}

.purchase-dialog :deep(.v-label) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.purchase-dialog :deep(.v-autocomplete .v-field__append-inner i),
.purchase-dialog :deep(.v-text-field .v-field__append-inner i) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.purchase-dialog :deep(.v-list) {
  background-color: #2B2D33 !important;
}

.purchase-dialog :deep(.v-list-item) {
  color: #fff !important;
}

.purchase-dialog :deep(.v-list-item:hover) {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.purchase-dialog :deep(.v-list-item-title) {
  color: #fff !important;
}

.purchase-dialog :deep(.v-list-item-subtitle) {
  color: rgba(255, 255, 255, 0.6) !important;
}

.purchase-dialog :deep(.v-select .v-field__input),
.purchase-dialog :deep(.v-select .v-field__field),
.purchase-dialog :deep(.v-select input),
.purchase-dialog :deep(.v-select .v-select__selection-text),
.purchase-dialog :deep(.v-select .v-select__selection),
.purchase-dialog :deep(.v-select .v-field__append-inner) {
  color: #fff !important;
}

.purchase-dialog :deep(.v-select .v-field__outline) {
  color: rgba(255, 255, 255, 0.12) !important;
}

.purchase-dialog :deep(.v-select .v-field__append-inner i) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.purchase-dialog :deep(.v-messages__message) {
  color: #ff5252 !important;
}

.purchase-dialog :deep(input[type="number"])::-webkit-inner-spin-button,
.purchase-dialog :deep(input[type="number"])::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.purchase-dialog :deep(input[type="number"]) {
  -moz-appearance: textfield;
}

.purchase-dialog :deep(.mdi) {
  color: #fff !important;
}

.material-purchase-list {
  margin: 20px 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  overflow: hidden;
}

.material-purchase-body {
  max-height: 400px;
  overflow-y: auto;
}

.material-purchase-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: background-color 0.2s;
}

.material-purchase-item:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.material-purchase-item:last-child {
  border-bottom: none;
}

.material-purchase-item .item-content {
  color: #fff;
  font-size: 14px;
  text-align: center;
  padding: 0 4px;
}

.material-purchase-item .material-name {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
}

.material-purchase-item .material-spec {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  text-align: left;
  margin-top: 2px;
}

.material-purchase-item .material-code {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  text-align: left;
  margin-top: 4px;
}

.native-date-field {
  cursor: pointer;
}

:deep(.v-field--readonly) {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

:deep(.v-field--readonly .v-field__input) {
  color: rgba(255, 255, 255, 0.5) !important;
}
</style>
